import {
  ComponentType,
  DashboardState,
  DragDropInsertMethod,
  EScreenType,
  ESdChangeReason,
  EUserAccessPart,
  IComponentDefinition,
  IDisplayItem,
  IDisplayItemComponent,
  IDragDropByMoveItemMessage,
  SpecialDIIds,
  ViewPart
} from "@kuika/kuika-cl-model";
import Tree from "rc-tree";
import "rc-tree/assets/index.css";
import { DataNode, TreeNodeProps } from "rc-tree/lib/interface";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { ScreenDesignHelper } from "../../../../shared/utility/screen-design-helper";
import {
  canMoveDown,
  canMoveUp,
  getMoveDownReorder,
  getMoveUpReorder,
  ISiblingReorder
} from "../../../../shared/utility/sibling-reorder";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaComponentIcon from "../../../ui-kit/kuika-component-icon/kuika-component-icon";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaOverflowText from "../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaSelectionIndicator from "../../../ui-kit/kuika-selection-indicator/kuika-selection-indicator";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import SearchablePanelHeader from "../searchable-panel-header/searchable-panel-header";
import "./component-tree.scss";

import { TREE_COLLAPSE_MOTION, TREE_SELECTED_NODE_SELECTOR } from "./component-tree-motion";
export interface IComponentTreeState {
  isSearchActive: boolean;
  searchText: string;
  expandedKeys: string[];
  selectedKeys: string[];
  draggingElementId?: string;
  /** A node is being dragged: the selection indicator steps aside until the drag ends. */
  isNodeDragging: boolean;
  isRenaming: boolean;
  renamingId?: number;
  isRenameLoading: boolean;
}

export interface IComponentTreeProps {}

class ComponentTree extends PureSdComponent<IComponentTreeProps & IWithSDProps, IComponentTreeState> {
  constructor(props: IComponentTreeProps & IWithSDProps) {
    super(props);
    this.state = {
      isSearchActive: false,
      searchText: "",
      expandedKeys: [],
      selectedKeys: [],
      draggingElementId: undefined,
      isNodeDragging: false,
      isRenaming: false,
      isRenameLoading: false
    };
  }

  filteredKeys = [] as string[];

  componentDidUpdate(prevProps: IComponentTreeProps & IWithSDProps) {
    const oldKuikaId: string = prevProps.sd.selectedKuikaIds[0];
    const newKuikaId: string = this.props.sd.selectedKuikaIds[0];
    if (oldKuikaId !== newKuikaId && newKuikaId !== this.state.selectedKeys[0]) {
      this.setAsSelectedItemFromIframe(newKuikaId.toString(), false);
    }
  }

  componentDidMount() {
    this.setAsSelectedItemFromIframe(this.props.sd.selectedKuikaIds[0].toString(), false);
    this.handleSearchTextOnInit();
  }

  handleSearchTextOnInit = () => {
    const searchText = this.props.sd.searchTexts[ViewPart.DisplayItemTreePanel];
    if (searchText && searchText.length > 0) {
      this.handleSearchInputChange(searchText);
      this.setState({ isSearchActive: true });
    }
  };

  handleSearchInputChange = (searchText: string): void => {
    this.filteredKeys = [];
    if (searchText) {
      const rootDI = this.props.sd.screenDesignDictionary[SpecialDIIds.rootDI];
      const headerDI = this.props.sd.screenDesignDictionary[SpecialDIIds.headerDI];
      const footerDI = this.props.sd.screenDesignDictionary[SpecialDIIds.footerDI];
      const pageHeaderDI = this.props.sd.screenDesignDictionary[SpecialDIIds.pageHeaderDI];
      const pageFooterDI = this.props.sd.screenDesignDictionary[SpecialDIIds.pageFooterDI];
      if (rootDI) this.searchInTree(rootDI, searchText);
      if (headerDI) this.searchInTree(headerDI, searchText);
      if (pageHeaderDI) this.searchInTree(pageHeaderDI, searchText);
      if (footerDI) this.searchInTree(footerDI, searchText);
      if (pageFooterDI) this.searchInTree(pageFooterDI, searchText);

      if (this.props.sd.getScreenType() === EScreenType.Master) {
        const layoutHeaderDI = this.props.sd.screenDesignDictionary[SpecialDIIds.layoutHeaderDI];
        const layoutLeftDI = this.props.sd.screenDesignDictionary[SpecialDIIds.layoutLeftDI];
        const layoutRightDI = this.props.sd.screenDesignDictionary[SpecialDIIds.layoutRightDI];
        const layoutFooterDI = this.props.sd.screenDesignDictionary[SpecialDIIds.layoutFooterDI];

        if (layoutHeaderDI) this.searchInTree(layoutHeaderDI, searchText);
        if (layoutLeftDI) this.searchInTree(layoutLeftDI, searchText);
        if (layoutRightDI) this.searchInTree(layoutRightDI, searchText);
        if (layoutFooterDI) this.searchInTree(layoutFooterDI, searchText);
      }
    }

    this.setState({
      expandedKeys: this.filteredKeys,
      searchText
    });
    this.props.sd.setSearchTexts(ViewPart.DisplayItemTreePanel, searchText);
  };

  deleteSearchText = (e): void => {
    e.stopPropagation();
    this.filteredKeys = [];
    this.props.sd.setSearchTexts(ViewPart.DisplayItemTreePanel, "");
    this.setState({ searchText: "", isSearchActive: false, expandedKeys: [] });
  };

  searchInTree = (item: IDisplayItem, searchText: string): void => {
    const searchResult: boolean = this.getSearchResult(item, searchText);

    if (searchResult) {
      this.addAncestorKeys(item);
      this.filteredKeys.push(item.id.toString());
    }

    if (item.children) {
      item.children.forEach((child) => {
        this.searchInTree(child, searchText);
      });
    }
  };

  addAncestorKeys = (item: IDisplayItem): void => {
    let currentItem = item;
    while (currentItem.parentId) {
      if (!this.filteredKeys.includes(currentItem.parentId.toString())) {
        this.filteredKeys.push(currentItem.parentId.toString());
      }
      const parentItem = this.props.sd.screenDesignDictionary[currentItem.parentId];
      if (!parentItem) break;
      currentItem = parentItem;
    }
  };

  getSearchResult = (item: IDisplayItem, searchText: string): boolean => {
    return (
      item?.friendlyName?.toLowerCase().includes(searchText.toLowerCase()) || item.id.toString().includes(searchText)
    );
  };

  getComponentIcon = (item: IDisplayItem): JSX.Element => {
    const definition: IComponentDefinition | undefined = this.props.sd.getComponentDefinition(item.item);
    return (
      <KuikaComponentIcon
        src={KMainFunctions.getImageUrlByComponentDefinition(definition)}
        width={20}
        height={20}
        className="component-tree__child-icon"
      />
    );
  };

  setDiAsSelectedItem = (kuikaId: string): void => {
    this.setAsSelectedItemFromIframe(kuikaId, true);
    this.handleScrollIntoElement(".rc-tree-treenode-selected", { block: "nearest", inline: "nearest" });
  };

  handleHighlightHoveredItem = (node: IDisplayItem): void => {
    if (node && node.id) {
      this.props.sd.highlightHoveredItem(node.id.toString());
    }
  };

  setAsSelectedItemFromIframe = (kuikaId: string, isSelectedFromTree?: boolean): void => {
    if (kuikaId === SpecialDIIds.rootDI.toString()) {
      if (this.state.selectedKeys.length > 0) {
        this.setState({ selectedKeys: [] });
      }
      return;
    }
    if (this.state.selectedKeys[0] === kuikaId) return;
    let expandedKeys: string[] = [];
    this.state.expandedKeys.forEach((key: string) => {
      expandedKeys.push(key);
    });
    expandedKeys = this.getAllParentsToExpand(kuikaId, expandedKeys);
    this.props.sd.setSelectedDisplayItem(kuikaId);
    this.setState({ expandedKeys, selectedKeys: [kuikaId] }, () => {
      if (!isSelectedFromTree) {
        this.handleScrollIntoElement(".rc-tree-treenode-selected", { block: "nearest", inline: "nearest" });
      }
    });
  };

  getAllParentsToExpand = (kuikaId: string, expandedKeys: string[]): string[] => {
    if (kuikaId !== SpecialDIIds.rootDI.toString() && this.props.sd.getScreenDesign().displayItems) {
      const targetDI: IDisplayItem | undefined = this.props.sd.screenDesignDictionary[kuikaId];
      if (targetDI && targetDI.parentId && targetDI.parentId !== SpecialDIIds.rootDI) {
        const parent = this.props.sd.screenDesignDictionary[targetDI.parentId];
        if (parent) {
          expandedKeys.push(parent.id.toString());
          this.getAllParentsToExpand(parent.id.toString(), expandedKeys);
        }
      }
    }
    return expandedKeys;
  };

  // Callers must pass block/inline "nearest": scrollIntoView walks *every* scrollable
  // ancestor, so an aligning value ("start"/"end") also scrolls the outer designer
  // layout and visibly shifts the whole screen when the tree reveals the selected node.
  handleScrollIntoElement = (selector: string, arg?: boolean | ScrollIntoViewOptions | undefined) => {
    setTimeout(() => {
      const node: Element | null = document.querySelector(selector);
      if (!node) {
        this.handleScrollIntoElement(selector, arg);
        return;
      }
      node.scrollIntoView(arg);
    }, 50);
  };

  onDragStart = (event: any): void => {
    this.setState({ draggingElementId: event?.node?.key, isNodeDragging: true });
  };

  // rc-tree reports the end of every drag, dropped on a node or not.
  onDragEnd = (): void => {
    this.setState({ isNodeDragging: false });
  };

  onDrop = (info: any): void => {
    const targetKuikaId = info.node.key;
    const sourceKuikaId = info.dragNode.key;
    const targetItem = this.props.sd.screenDesignDictionary[parseInt(targetKuikaId, 10)];
    let targetKuikaItemId: string = "";

    const item: IDisplayItemComponent | undefined = targetItem?.item;
    const itemDefinition: IComponentDefinition | undefined = this.props.sd.getComponentDefinition(item);
    if (itemDefinition && this.isItDroppableContainer(itemDefinition.componentType)) {
      targetKuikaItemId = `${targetKuikaId.toString()}-lastChildItem`;
    } else {
      targetKuikaItemId = targetKuikaId.toString();
    }

    if (
      info.dropToGap ||
      info.node.dragOverGapBottom ||
      (info.node.dragOverGapTop && itemDefinition && this.isItDroppableContainer(itemDefinition.componentType))
    ) {
      this.props.sd?.reOrderItems(sourceKuikaId, targetKuikaItemId, sourceKuikaId);
    } else if (info.node.dragOver && itemDefinition && this.isItDroppableContainer(itemDefinition.componentType)) {
      const message = {
        sourceKuikaId: sourceKuikaId.toString(),
        targetKuikaId: targetKuikaItemId,
        insertMethod: DragDropInsertMethod.insertInto
      };
      this.props.sd.dragDropByMoveItemMessage(message);
    } else return KMainFunctions.displayWarningNotification("You can not drop item here.");
  };

  isItDroppableContainer = (componentType: ComponentType): boolean => {
    if (
      componentType === ComponentType.abstractContainer ||
      componentType === ComponentType.container ||
      componentType === ComponentType.responsiveColumn ||
      componentType === ComponentType.fixedHeader ||
      componentType == ComponentType.fixedFooter ||
      componentType == ComponentType.pageHeader ||
      componentType == ComponentType.pageFooter ||
      componentType == ComponentType.layoutLeft ||
      componentType == ComponentType.layoutRight
    ) {
      return true;
    }
    return false;
  };

  onExpand = (keys: any[]): void => {
    const stringKeys = keys.map((key) => key.toString());
    this.setState({ expandedKeys: stringKeys });
  };

  isMicroApp = (node: IDisplayItem | undefined): boolean => {
    const item: IDisplayItemComponent | undefined = node?.item;
    const itemDefinition: IComponentDefinition | undefined = this.props.sd.getComponentDefinition(item);
    if (itemDefinition && itemDefinition.name === "MicroApp") {
      return true;
    }
    return false;
  };

  isFormComponent = (node: IDisplayItem | undefined): boolean => {
    return node?.item?.name === "Form";
  };

  isInteractionDisabled = (node: IDisplayItem | undefined): boolean => {
    return ScreenDesignHelper.getIsInteractionDisabled(node?.id ?? 0, this.props.sd.screenDesignDictionary);
  };

  isParentsChildrenInteractionDisabled = (node: IDisplayItem | undefined): boolean => {
    return ScreenDesignHelper.getIsParentsChildrenInteractionDisabled(
      node?.id ?? 0,
      this.props.sd.screenDesignDictionary
    );
  };

  isChildOfFormComponent = (node: IDisplayItem | undefined): boolean => {
    return ScreenDesignHelper.getIsChildOfForm(node?.id ?? 0, this.props.sd.screenDesignDictionary);
  };

  isModalOrDrawerComponent = (node: IDisplayItem | undefined): boolean => {
    if (!node || !node.item) return false;

    const componentName = node.item.name;
    return componentName === "Modal" || componentName === "Drawer" || componentName === "FloatPanel";
  };

  renderNodeMenu = (node: IDisplayItem): KuikaDropdownMenuItemType[] => {
    const options: KuikaDropdownMenuItemType[] = [];

    if (this.isItemRenamable(node.id)) {
      options.push({
        key: "rename",
        label: "Rename",
        icon: <KuikaSvg width={16} height={16} name="edit" />,
        onClick: () => this.renameItem(new Event("click"), node)
      });
    }

    if (node?.id !== SpecialDIIds.rootDI) {
      if (!this.isMicroApp(node)) {
        options.push({
          key: "copy",
          label: "Copy",
          icon: <KuikaSvg width={16} height={16} name="copy" />,
          onClick: () => this.copyItem(new Event("click"), node)
        });
      }
      if (
        this.getClipboardValue() &&
        !this.isMicroApp(node) &&
        !this.isFormComponent(node) &&
        !this.isInteractionDisabled(node) &&
        !this.isParentsChildrenInteractionDisabled(node) &&
        !this.isChildOfFormComponent(node)
      ) {
        options.push({
          key: "paste",
          label: "Paste",
          icon: <KuikaSvg width={16} height={16} name="paste" />,
          onClick: () => this.pasteItem(new Event("click"), node),
          disabled: !this.getClipboardValue()
        });
      }
      if (this.getClipboardValue() && !this.isMicroApp(node)) {
        options.push({
          key: "paste-styling",
          label: "Paste Styling",
          icon: <KuikaSvg width={16} height={16} name="paste" />,
          onClick: () => this.pasteItemStyling(new Event("click"), node),
          disabled: !this.getClipboardValue()
        });
      }
      if (
        node?.id !== SpecialDIIds.headerDI &&
        node?.id !== SpecialDIIds.footerDI &&
        node?.id !== SpecialDIIds.pageHeaderDI &&
        node?.id !== SpecialDIIds.pageFooterDI &&
        !this.isMicroApp(node) &&
        !this.isFormComponent(node) &&
        !this.isInteractionDisabled(node) &&
        !this.isParentsChildrenInteractionDisabled(node) &&
        !this.isChildOfFormComponent(node) &&
        !this.props.sd.isElementLocked(node.id)
      ) {
        options.push({
          key: "clone",
          label: "Duplicate",
          icon: <KuikaSvg width={16} height={16} name="copy-plus" />,
          onClick: () => this.cloneItem(new Event("click"), node)
        });
      }
      if (
        !this.isInteractionDisabled(node) &&
        !this.isParentsChildrenInteractionDisabled(node) &&
        !this.isChildOfFormComponent(node) &&
        !this.props.sd.isElementLocked(node.id)
      ) {
        options.push({
          key: "delete",
          label: <KuikaText>Delete</KuikaText>,
          icon: <KuikaSvg width={16} height={16} name="trash" />,
          onClick: () => this.deleteItem(new Event("click"), node),
          danger: true
        });
      }
    }

    return options;
  };

  isItemRenamable = (id?: number) => {
    const di = this.props.sd.screenDesignDictionary[id ?? parseInt(this.props.sd.selectedKuikaIds[0], 10)];
    const isLayoutComponent = this.isLayoutComponent(di);
    if (isLayoutComponent) return false;
    if (di && di.item) {
      const diItem = di.item;
      const compDef = this.props.sd.getComponentDefinition(diItem);
      if (compDef?.isRenamingDisabled === true) return false;
    }
    return true;
  };

  isLayoutComponent = (node: IDisplayItem | undefined): boolean => {
    const item: IDisplayItemComponent | undefined = node?.item;
    const itemDefinition: IComponentDefinition | undefined = this.props.sd.getComponentDefinition(item);
    if (
      itemDefinition &&
      (itemDefinition.componentType === ComponentType.layoutLeft ||
        itemDefinition.componentType === ComponentType.layoutRight ||
        itemDefinition.componentType === ComponentType.layoutHeader ||
        itemDefinition.componentType === ComponentType.layoutFooter)
    ) {
      return true;
    }
    return false;
  };

  isEditableTableColumnChild = (): boolean => {
    const sd = this.props.sd.getScreenDesign();
    const selectedDisplayItem: IDisplayItem | undefined = this.props.sd.getSelectedDisplayItem(sd);

    const selectedDIParent = selectedDisplayItem?.parentId
      ? this.props.sd.screenDesignDictionary[selectedDisplayItem?.parentId]
      : undefined;
    const isEditableTableColumnChild =
      selectedDIParent?.item?.name === "EditableTableColumn" || selectedDIParent?.item?.name === "TableDefaultColumn";
    return isEditableTableColumnChild;
  };

  renameItem = (e: any, item: IDisplayItem) => {
    e.stopPropagation();
    this.setState({ renamingId: item.id, isRenaming: true }, () => {
      this.handleScrollIntoElement(".rc-tree-treenode-selected", { block: "nearest", inline: "nearest" });
    });
  };

  copyItem = (e: any, item: IDisplayItem) => {
    e.stopPropagation();
    const sourceKuikaId = item.id.toString();
    const message = {
      kuikaid: sourceKuikaId
    };
    this.props.sd.copyItemMessage(message);
    this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
  };

  pasteItem = (e: any, item: IDisplayItem) => {
    e.stopPropagation();
    const sourceKuikaId = item.id.toString();
    const message = {
      kuikaid: sourceKuikaId
    };
    this.props.sd.pasteItemMessage(message);
    this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
  };

  pasteItemStyling = (e: any, item: IDisplayItem) => {
    e.stopPropagation();
    const sourceKuikaId = item.id.toString();
    const message = {
      kuikaid: sourceKuikaId
    };
    this.props.sd.pasteItemStyleMessage(message);
    this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
  };

  cloneItem = (e: any, item: IDisplayItem) => {
    e.stopPropagation();
    const sourceKuikaId = item.id.toString();
    const message = {
      kuikaid: sourceKuikaId.toString()
    };
    this.props.sd.cloneItemMessage(message);
    this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
  };

  deleteItem = async (e: any, item: IDisplayItem) => {
    e.stopPropagation();

    const interactionDisabledFriendlyNames =
      ScreenDesignHelper.getFriendlyNamesOfChildDisplayItemsWhichInteractionsDisabled(
        item.id,
        this.props.sd.screenDesignDictionary
      );

    if (interactionDisabledFriendlyNames.length > 0) {
      KMainFunctions.displayWarningNotification(
        `This element can not be deleted as it contains some elements with disabled interactions. Move these elements to another parent element and try again: ${interactionDisabledFriendlyNames.join(
          ", "
        )}`
      );
      return;
    }

    const sourceKuikaId = item.id.toString();
    const message = {
      kuikaid: sourceKuikaId
    };
    this.props.sd.removeItemMessage(message);
    this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
  };

  getClipboardValue = (): any => {
    return this.props.sd.getClipboardValue();
  };

  // Move up / down share their rules with the canvas selection overlay's ⋯ menu (sibling-reorder.ts).
  private resolveDefinition = (item: IDisplayItemComponent): IComponentDefinition | undefined =>
    this.props.sd.getComponentDefinition(item);

  canMoveUpNode = (kuikaId: number): boolean =>
    canMoveUp(this.props.sd.screenDesignDictionary, kuikaId, this.resolveDefinition);

  canMoveDownNode = (kuikaId: number): boolean =>
    canMoveDown(this.props.sd.screenDesignDictionary, kuikaId, this.resolveDefinition);

  moveUpNode = (kuikaId: number): void => {
    const reorder: ISiblingReorder | null = getMoveUpReorder(this.props.sd.screenDesignDictionary, kuikaId);
    if (reorder) {
      this.reOrderItems(reorder.sourceKuikaId, reorder.targetKuikaId, reorder.nextSelectedKuikaId);
    }
  };

  moveDownNode = (kuikaId: number): void => {
    const reorder: ISiblingReorder | null = getMoveDownReorder(this.props.sd.screenDesignDictionary, kuikaId);
    if (reorder) {
      this.reOrderItems(reorder.sourceKuikaId, reorder.targetKuikaId, reorder.nextSelectedKuikaId);
    }
  };

  reOrderItems = (sourceKuikaId: string, targetKuikaId: string, nextSelectedNodeDi: string) => {
    let selectedKuikaId = "";
    const sd = this.props.sd.getScreenDesign();
    const selectedDisplayItem = this.props.sd.getSelectedDisplayItem(sd);
    if (selectedDisplayItem && selectedDisplayItem.id) {
      selectedKuikaId = selectedDisplayItem.id.toString();
    }
    if (this.props.sd && this.props.sd.reOrderItems) {
      this.props.sd.reOrderItems(sourceKuikaId, targetKuikaId, nextSelectedNodeDi);
    }
  };

  handleRenameInputOnSubmit = (e) => {
    let newName: any = e.target?.value ?? e.state.value;
    if (
      ScreenDesignHelper.isDiFriendlyNameUnique(
        this.props.sd.getScreenDesign().displayItems,
        this.props.sd.selectedKuikaIds[0],
        newName
      ) === false
    ) {
      KMainFunctions.displayWarningNotification("This name is not unique. Please retry with another name.");
      this.handleNameOnBlur(e);
      return;
    }
    const nameRules = /^[A-Za-z0-9-_ ]+$/;
    const isNameValid = nameRules.test(newName);
    if (!isNameValid) {
      KMainFunctions.displayWarningNotification(
        "Name is invalid. Name should contain only letters, numbers, spaces, underscores and dashes."
      );
      this.setState({ isRenameLoading: false }, () => {
        this.props.sd.setRenamingDisplayItemId();
      });
      return;
    }
    this.changeDiFriendlyName(newName);
  };

  changeDiFriendlyName = (newName: string) => {
    if (newName && newName.length > 0) {
      const sd = this.props.sd.getScreenDesign();
      const di = this.state.renamingId ? this.props.sd.screenDesignDictionary[this.state.renamingId] : undefined;

      if (di) {
        di.friendlyName = newName;
        this.setState({ isRenaming: false, renamingId: undefined }, () => {
          this.props.sd.setScreenDesign(sd, ESdChangeReason.ChangeDiFriendlyName);
          this.props.sd.setRenamingDisplayItemId();
        });
      }
    }
  };

  handleNameOnBlur = async (e) => {
    const newName = e.target.value.trim();
    if (newName) {
      this.setState({ isRenameLoading: true });
      this.handleRenameInputOnSubmit(e);
      this.setState({ isRenameLoading: false, isRenaming: false, renamingId: undefined });
    } else {
      this.setState({ isRenaming: false, renamingId: undefined });
    }
  };

  handleNameKeypress = (e) => {
    if (e.keyCode === 13) {
      this.handleNameOnBlur(e);
    } else if (e.keyCode === 27) {
      this.setState({ isRenaming: false, renamingId: undefined });
    }
  };

  convertToTreeData = (node: IDisplayItem | IDisplayItem[] | undefined): DataNode[] => {
    if (!node) return [];

    if (Array.isArray(node)) {
      return node.flatMap((child) => this.convertToTreeData(child));
    }

    if (this.isModalOrDrawerComponent(node)) {
      return [];
    }

    const isSelected = this.state.selectedKeys.includes(node.id.toString());
    const componentIcon: JSX.Element = this.getComponentIcon(node);
    const hasUserAccessEditComponet = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.CreateComponent,
      this.props.sd.project?.projectAccess
    );
    const isSearchMatch = this.state.searchText && this.getSearchResult(node, this.state.searchText);
    const isHidden: boolean = this.props.sd.isElementHidden(node.id);
    const isLocked: boolean = this.props.sd.isElementLocked(node.id);

    return [
      {
        key: node.id.toString(),
        // rc-tree puts this on the treenode, which is the element that carries the row hover: a
        // clipped layer name then slides while the row's hover-only lock/visibility buttons show.
        className: OVERFLOW_REVEAL_HOST_CLASS,
        title: (
          <div
            className="kuika-component-tree__title"
            data-testid="itemTreeComponentRow"
            // Design-time hidden state is otherwise only expressed through a styling
            // class (line-through + dimmed) and the eye/eye-off icon. Expose it as an
            // explicit row attribute so the state can be read without depending on
            // CSS internals — the canvas agent does the same with
            // `data-design-time-hidden` on the hidden element.
            data-hidden={isHidden ? "true" : "false"}
            // Same reasoning for the design-time locked state: it is otherwise only
            // expressed through an active-styling class on the lock button and the
            // lock/lock-open icon name. The canvas consumes the same state to turn
            // `draggable` off, so the row states it explicitly instead.
            data-locked={isLocked ? "true" : "false"}
            onClick={(e) => {
              e.stopPropagation();
              this.setDiAsSelectedItem(node.id.toString());
            }}
            onMouseEnter={() => this.handleHighlightHoveredItem(node)}
          >
            <div
              className={`kuika-component-tree__title-left ${isSearchMatch ? "search-match" : ""} ${isHidden ? "kuika-component-tree__title-left--hidden" : ""}`}
            >
              {componentIcon}
              {this.state.isRenaming && this.state.renamingId === node.id ? (
                <KuikaInput
                  data-testid="itemTreeRenameInput"
                  data-onboarding-id="left-panel_component-tree_rename-input"
                  defaultValue={node.friendlyName}
                  onKeyDown={this.handleNameKeypress}
                  onBlur={this.handleNameOnBlur}
                  disabled={this.state.isRenameLoading}
                  autoFocus
                />
              ) : (
                <KuikaText
                  data-testid={node.friendlyName}
                  data-onboarding-id={`left-panel_component-tree_${node.friendlyName}`}
                  // KuikaText only derives its native title from plain-string children, and the
                  // reveal wrapper is an element — state it so the E2E row selectors keep working.
                  title={node.friendlyName}
                >
                  <KuikaOverflowText>{node.friendlyName}</KuikaOverflowText>
                </KuikaText>
              )}
              {node.id !== SpecialDIIds.rootDI && (
                <div className="kuika-component-tree__title-actions">
                  <KuikaTooltip content={isLocked ? "Unlock" : "Lock"} instantExit>
                    <KuikaButton
                      data-testid="lockToggle"
                      data-onboarding-id="left-panel_component-tree_lock-toggle"
                      type="tertiary"
                      color="neutral"
                      isIcon
                      aria-label={isLocked ? "Unlock" : "Lock"}
                      className={
                        isLocked ? "kuika-component-tree__action-btn--active" : "kuika-component-tree__action-btn"
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        this.props.sd.toggleElementLocked(node.id);
                      }}
                    >
                      <KuikaSvg name={isLocked ? "lock" : "lock-open"} width={16} height={16} />
                    </KuikaButton>
                  </KuikaTooltip>
                  <KuikaTooltip content={isHidden ? "Show" : "Hide"} instantExit>
                    <KuikaButton
                      data-testid="visibilityToggle"
                      data-onboarding-id="left-panel_component-tree_visibility-toggle"
                      type="tertiary"
                      color="neutral"
                      isIcon
                      aria-label={isHidden ? "Show" : "Hide"}
                      className={
                        isHidden ? "kuika-component-tree__action-btn--active" : "kuika-component-tree__action-btn"
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        this.props.sd.toggleElementHidden(node.id);
                      }}
                    >
                      <KuikaSvg name={isHidden ? "eye-off" : "eye"} width={16} height={16} />
                    </KuikaButton>
                  </KuikaTooltip>
                </div>
              )}
              {!this.isEditableTableColumnChild() && hasUserAccessEditComponet && this.isItemRenamable(node.id) && (
                <div className="kuika-component-tree__title-right">
                  {(this.props.sd.getScreenType() !== EScreenType.Report || node.item?.name === "StaticTableRow") &&
                    this.canMoveUpNode(node.id) &&
                    !isLocked && (
                      <KuikaTooltip content="Move up" instantExit>
                        <KuikaButton
                          data-testid="upArrow"
                          data-onboarding-id="left-panel_component-tree_up-arrow"
                          type="tertiary"
                          color="neutral"
                          isIcon
                          aria-label="Move up"
                          onClick={() => this.moveUpNode(node.id)}
                        >
                          <KuikaSvg name="arrow-up" type="arrows" width={16} height={16} />
                        </KuikaButton>
                      </KuikaTooltip>
                    )}
                  {(this.props.sd.getScreenType() !== EScreenType.Report || node.item?.name === "StaticTableRow") &&
                    this.canMoveDownNode(node.id) &&
                    !isLocked && (
                      <KuikaTooltip content="Move down" instantExit>
                        <KuikaButton
                          data-testid="downArrow"
                          data-onboarding-id="left-panel_component-tree_down-arrow"
                          type="tertiary"
                          color="neutral"
                          isIcon
                          aria-label="Move down"
                          onClick={() => this.moveDownNode(node.id)}
                        >
                          <KuikaSvg name="arrow-down" type="arrows" width={16} height={16} />
                        </KuikaButton>
                      </KuikaTooltip>
                    )}
                  <KuikaDropdownMenu
                    options={this.renderNodeMenu(node)}
                    overlayClassName="kuika-component-tree__dropdown"
                  >
                    {(open) => (
                      <KuikaButton
                        data-testid="threeDotMenuButtonOnItemTree"
                        data-onboarding-id="left-panel_component-tree_menu"
                        type="tertiary"
                        color="neutral"
                        isIcon
                        active={open}
                      >
                        <KuikaSvg name="dots" width={16} height={16} />
                      </KuikaButton>
                    )}
                  </KuikaDropdownMenu>
                </div>
              )}
            </div>
          </div>
        ),
        children:
          node.children
            ?.flatMap((child, index) => {
              if (node.id === SpecialDIIds.rootDI && index === 0) return [];
              return this.convertToTreeData(child);
            })
            .filter((child) => child) || []
      }
    ];
  };

  handleAllowDrop = ({ dropNode, dropPosition }) => {
    const targetDI = this.props.sd.screenDesignDictionary[parseInt(dropNode.key.toString(), 10)];
    const sourceDI = this.props.sd.screenDesignDictionary[parseInt(this.state.draggingElementId || "0", 10)];

    if (!targetDI || !sourceDI) return false;

    if (this.props.sd.isElementLocked(sourceDI.id)) return false;

    const targetComponent = targetDI.item;
    const targetComponentDefinition = this.props.sd.getComponentDefinition(targetComponent);
    const sourceComponentDefinition = this.props.sd.getComponentDefinition(sourceDI.item);

    const message: IDragDropByMoveItemMessage = {
      sourceKuikaId: this.state.draggingElementId || "",
      targetKuikaId: dropNode.key.toString(),
      insertMethod: dropPosition === 0 ? DragDropInsertMethod.insertInto : DragDropInsertMethod.insertAfter
    };

    const isElementDraggable = this.props.sd.getElementIsDraggable(
      targetComponent,
      targetComponentDefinition,
      sourceComponentDefinition,
      targetDI,
      this.props.sd.getScreenDesign(),
      this.props.sd.screenDesignDictionary,
      message,
      sourceDI,
      dropNode.key.toString(),
      this.state.draggingElementId || "",
      true
    );

    const isItDroppableContainer = this.isItDroppableContainer(
      targetComponentDefinition?.componentType as ComponentType
    );

    return isElementDraggable && isItDroppableContainer;
  };

  sortChildrenByYPosition = (children: IDisplayItem[]): IDisplayItem[] => {
    return [...children].sort((a, b) => {
      const aY = a.styleObj?.position?.yAxis ?? 0;
      const bY = b.styleObj?.position?.yAxis ?? 0;
      return aY - bY;
    });
  };

  getLockedHiddenMenuOptions = (): KuikaDropdownMenuItemType[] => {
    const dict = this.props.sd.screenDesignDictionary;
    const options: KuikaDropdownMenuItemType[] = [];

    this.props.sd.hiddenElementIds.forEach((id) => {
      const di = dict[id];
      if (!di) return;
      options.push({
        key: `hidden-${id}`,
        label: di.friendlyName || di.item?.name || `Element ${id}`,
        icon: <KuikaSvg name="eye-off" width={14} height={14} />,
        group: "Hidden Items",
        onClick: () => {
          this.setDiAsSelectedItem(id.toString());
        },
        rightIcon: (
          <KuikaButton
            isIcon
            type="tertiary"
            color="neutral"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              this.props.sd.toggleElementHidden(id);
            }}
          >
            <KuikaSvg name="eye" width={14} height={14} />
          </KuikaButton>
        )
      });
    });

    this.props.sd.lockedElementIds.forEach((id) => {
      const di = dict[id];
      if (!di) return;
      options.push({
        key: `locked-${id}`,
        label: di.friendlyName || di.item?.name || `Element ${id}`,
        icon: <KuikaSvg name="lock" width={14} height={14} />,
        group: "Locked Items",
        onClick: () => {
          this.setDiAsSelectedItem(id.toString());
        },
        rightIcon: (
          <KuikaButton
            isIcon
            type="tertiary"
            color="neutral"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              this.props.sd.toggleElementLocked(id);
            }}
          >
            <KuikaSvg name="lock-open" width={14} height={14} />
          </KuikaButton>
        )
      });
    });

    return options;
  };

  render() {
    const rootDI = this.props.sd.screenDesignDictionary[SpecialDIIds.rootDI];
    const headerDI = this.props.sd.screenDesignDictionary[SpecialDIIds.headerDI];
    const footerDI = this.props.sd.screenDesignDictionary[SpecialDIIds.footerDI];
    const pageHeaderDI = this.props.sd.screenDesignDictionary[SpecialDIIds.pageHeaderDI];
    const pageFooterDI = this.props.sd.screenDesignDictionary[SpecialDIIds.pageFooterDI];
    const layoutLeftDI = this.props.sd.screenDesignDictionary[SpecialDIIds.layoutLeftDI];
    const layoutRightDI = this.props.sd.screenDesignDictionary[SpecialDIIds.layoutRightDI];
    const layoutHeaderDI = this.props.sd.screenDesignDictionary[SpecialDIIds.layoutHeaderDI];
    const layoutFooterDI = this.props.sd.screenDesignDictionary[SpecialDIIds.layoutFooterDI];
    const isReportDesigner = this.props.sd.dashboardState === DashboardState.reportDesigner;

    const rootChildren = rootDI?.children
      ? rootDI.children.filter(
          (child) =>
            child.id !== SpecialDIIds.headerDI &&
            child.id !== SpecialDIIds.footerDI &&
            child.id !== SpecialDIIds.pageHeaderDI &&
            child.id !== SpecialDIIds.pageFooterDI
        )
      : [];
    const sortedRootChildren = isReportDesigner ? this.sortChildrenByYPosition(rootChildren) : rootChildren;

    const treeData = [
      ...(headerDI
        ? [
            {
              ...this.convertToTreeData(headerDI)[0],
              children: headerDI.children ? this.convertToTreeData(headerDI.children) : []
            }
          ]
        : []),

      ...(pageHeaderDI
        ? [
            {
              ...this.convertToTreeData(pageHeaderDI)[0],
              children: pageHeaderDI.children ? this.convertToTreeData(pageHeaderDI.children) : []
            }
          ]
        : []),

      ...(sortedRootChildren.length > 0 ? this.convertToTreeData(sortedRootChildren) : []),

      ...(pageFooterDI
        ? [
            {
              ...this.convertToTreeData(pageFooterDI)[0],
              children: pageFooterDI.children ? this.convertToTreeData(pageFooterDI.children) : []
            }
          ]
        : []),

      ...(footerDI
        ? [
            {
              ...this.convertToTreeData(footerDI)[0],
              children: footerDI.children ? this.convertToTreeData(footerDI.children) : []
            }
          ]
        : []),

      ...(this.props.sd.getScreenType() === EScreenType.Master
        ? [
            layoutHeaderDI && this.convertToTreeData(layoutHeaderDI)[0],
            layoutLeftDI && this.convertToTreeData(layoutLeftDI)[0],
            layoutRightDI && this.convertToTreeData(layoutRightDI)[0],
            layoutFooterDI && this.convertToTreeData(layoutFooterDI)[0]
          ].filter(Boolean)
        : [])
    ].filter(Boolean) as DataNode[];

    return (
      <div className="kuika-left-panel__container kuika-component-tree">
        <SearchablePanelHeader
          searchText={this.state.searchText}
          onSearchTextChange={(searchText: string) => {
            this.handleSearchInputChange(searchText);
          }}
          onSearchClearCallback={this.deleteSearchText}
          viewPart={ViewPart.DisplayItemTreePanel}
          panelName="Item Tree"
          isSearchActive={this.state.isSearchActive}
          setSearchActive={(isSearchActive: boolean) => {
            this.setState({ isSearchActive });
          }}
          extraRightUnit={
            this.props.sd.hiddenElementIds.size > 0 || this.props.sd.lockedElementIds.size > 0 ? (
              <KuikaDropdownMenu
                options={this.getLockedHiddenMenuOptions()}
                overlayClassName="kuika-component-tree__locked-hidden-dropdown"
                placement="bottomRight"
              >
                {(open) => (
                  <KuikaButton type="tertiary" color="neutral" size="sm" isIcon active={open}>
                    <KuikaSvg name="filter" width={16} height={16} />
                  </KuikaButton>
                )}
              </KuikaDropdownMenu>
            ) : undefined
          }
        />
        <div className="kuika-left-panel__content">
          {/* The tree's own wrapper, so the selected node's fill follows nodes opening above it. */}
          <div className="kuika-component-tree__rows">
            <KuikaSelectionIndicator activeSelector={TREE_SELECTED_NODE_SELECTOR} hidden={this.state.isNodeDragging} />
            {(!this.filteredKeys || this.filteredKeys.length === 0) &&
            this.state.searchText &&
            this.state.searchText.length > 0 &&
            treeData.length > 0 ? (
              <KuikaEmptyState
                svgName="search-off"
                customName="No Item found"
                description="We couldn't find any items matching your search."
              />
            ) : treeData.length === 0 ? (
              <KuikaEmptyState
                svgName="list-tree"
                svgType="sidebar-icons"
                customName="No Items"
                description="There are no items in the tree yet."
              />
            ) : (
              <Tree
                prefixCls="rc-tree"
                className="kuika-component-tree"
                treeData={treeData}
                motion={TREE_COLLAPSE_MOTION}
                expandedKeys={this.state.expandedKeys}
                selectedKeys={this.state.selectedKeys}
                onExpand={(keys) => this.onExpand(keys)}
                onSelect={(selectedKeys) => {
                  if (
                    selectedKeys.length > 0 &&
                    selectedKeys[0].toString() !== this.state.selectedKeys[0] &&
                    selectedKeys[0].toString() !== SpecialDIIds.rootDI.toString()
                  ) {
                    this.setDiAsSelectedItem(selectedKeys[0].toString());
                  }
                }}
                onDoubleClick={(e, node: any) => {
                  this.renameItem(e, node);
                }}
                draggable={(node) => !this.props.sd.isElementLocked(parseInt(node.key.toString(), 10))}
                onDragStart={this.onDragStart}
                onDragEnd={this.onDragEnd}
                onDrop={this.onDrop}
                allowDrop={this.handleAllowDrop}
                showIcon={false}
                switcherIcon={(nodeProps: TreeNodeProps) => {
                  const expanded: boolean = Boolean(nodeProps.expanded);
                  const isLeaf: boolean = Boolean(nodeProps.isLeaf);
                  // Only stamp a testid on real (non-leaf) switchers. rc-tree also
                  // calls switcherIcon for leaf nodes (rendered inside a noop
                  // switcher that ignores clicks); tagging those would make the
                  // E2E expand loop click dead carets forever.
                  let switcherTestId: string | undefined;
                  if (!isLeaf) {
                    switcherTestId = expanded ? "itemTreeSwitcherExpanded" : "itemTreeSwitcherCollapsed";
                  }
                  return (
                    <KuikaSvg
                      data-testid={switcherTestId}
                      name={expanded ? "k-down" : "k-right"}
                      type="arrows"
                      width={16}
                      height={16}
                    />
                  );
                }}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.ChangeDiFriendlyName ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.Undo ||
      changeReason === ESdChangeReason.Redo ||
      changeReason === ESdChangeReason.DesignTimeElementStateChanged
    ) {
      return true;
    }
    return false;
  };
}

const componentTree = withSD<IComponentTreeProps>(ComponentTree);
export { componentTree as ComponentTree };
